import React from "react";
import { Icon } from "../core/Icon.jsx";

/* Text/number input with label, helper text, error and disabled states.
   Focus → green border + ring. Error → red border + red helper. */
let _id = 0;

export const Input = React.forwardRef(function Input(
  {
    label,
    value,
    defaultValue,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    type = "text",
    error,
    helper,
    disabled = false,
    readOnly = false,
    iconLeft,
    prefix,
    suffix,
    required = false,
    size = "lg",
    align,
    multiline = false,
    rows,
    style,
    inputStyle,
    inputComponent: InputComponent,
    inputComponentProps,
    id: idProp,
    ...rest
  },
  ref
) {
  const [focus, setFocus] = React.useState(false);
  const autoId = React.useMemo(() => `tk-in-${++_id}`, []);
  const id = idProp || autoId;
  const height = size === "sm" ? 36 : size === "md" ? 44 : 48;
  const isDisabled = disabled || readOnly;

  let border = "inset 0 0 0 1px var(--border)";
  if (error) border = "inset 0 0 0 1.5px var(--red)";
  else if (focus) border = "inset 0 0 0 1.5px var(--green-bright)";

  const sharedInputStyle = {
    flex: 1,
    minWidth: 0,
    width: "100%",
    border: "none",
    outline: "none",
    boxShadow: "none",
    background: "transparent",
    fontFamily: "var(--font-sans)",
    fontWeight: 500,
    fontSize: 14,
    color: "var(--text-body)",
    textAlign: align || (type === "number" ? "right" : "left"),
    fontVariantNumeric: type === "number" ? "tabular-nums" : undefined,
    resize: multiline ? "vertical" : undefined,
    ...inputStyle,
  };

  const handleFocus = (e) => {
    setFocus(true);
    if (onFocus) onFocus(e);
  };

  const handleBlur = (e) => {
    setFocus(false);
    if (onBlur) onBlur(e);
  };

  let control = null;
  if (InputComponent) {
    const { style: inputComponentStyle, ...restInputComponentProps } = inputComponentProps || {};
    control = (
      <InputComponent
        id={id}
        value={value ?? ""}
        disabled={isDisabled}
        readOnly={readOnly}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        style={{ ...sharedInputStyle, ...inputComponentStyle }}
        getInputRef={ref}
        inputRef={ref}
        {...restInputComponentProps}
      />
    );
  } else if (multiline) {
    control = (
      <textarea
        ref={ref}
        id={id}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        rows={rows || 3}
        style={sharedInputStyle}
        {...rest}
      />
    );
  } else {
    control = (
      <input
        ref={ref}
        id={id}
        type={type}
        value={value}
        defaultValue={defaultValue}
        onChange={onChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        style={sharedInputStyle}
        {...rest}
      />
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      {label ? (
        <label htmlFor={id} style={{ fontFamily: "var(--font-sans)", fontWeight: 600, fontSize: 14, color: "var(--text-body)" }}>
          {label}{required ? <span style={{ color: "var(--red)" }}> *</span> : null}
        </label>
      ) : null}
      <div
        className="ds-input-box"
        style={{
          display: "flex",
          alignItems: multiline ? "stretch" : "center",
          gap: 8,
          minHeight: multiline ? undefined : height,
          height: multiline ? undefined : height,
          padding: multiline ? "10px 14px" : "0 14px",
          borderRadius: "var(--radius-xs)",
          background: isDisabled ? "var(--grey-bg)" : "var(--white)",
          boxShadow: isDisabled ? "inset 0 0 0 1px var(--border)" : border,
          transition: "box-shadow var(--dur) var(--ease)",
          cursor: isDisabled ? "not-allowed" : "text",
        }}
      >
        {iconLeft ? <Icon name={iconLeft} size={18} color="var(--text-muted)" /> : null}
        {prefix ? <span style={{ color: "var(--text-muted)", fontSize: 14, fontWeight: 500 }}>{prefix}</span> : null}
        {control}
        {suffix ? <span style={{ color: "var(--text-muted)", fontSize: 14, fontWeight: 500 }}>{suffix}</span> : null}
      </div>
      {error || helper ? (
        <div style={{ fontFamily: "var(--font-sans)", fontWeight: 500, fontSize: 12, color: error ? "var(--red)" : "var(--text-muted)" }}>
          {error || helper}
        </div>
      ) : null}
    </div>
  );
});

export default Input;
